import { useEffect, useMemo, useRef } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import * as THREE from 'three'

// ---------------------------------------------------------------------------
// Vertex shader — forwards world position so the fragment shader can build
// a proper per-pixel ray from the camera through the volume
// ---------------------------------------------------------------------------
const vertexShader = /* glsl */ `
  varying vec3 vWorldPos;

  void main() {
    vec4 worldPos = modelMatrix * vec4(position, 1.0);
    vWorldPos = worldPos.xyz;
    gl_Position = projectionMatrix * viewMatrix * worldPos;
  }
`

// ---------------------------------------------------------------------------
// Fragment shader — volumetric ray-march with physically-derived Mach diamonds
// Core maths ported from https://www.shadertoy.com/view/t3XyzB
// ---------------------------------------------------------------------------
const fragmentShader = /* glsl */ `
  precision highp float;

  uniform float uTime;
  uniform vec3  uCameraPos;
  uniform mat4  uModelMatrixInv;

  // Shape
  uniform float uNozzleRadius;
  uniform float uPressure;

  // Appearance
  uniform float uPlumeIntensity;
  uniform float uDiamondBrightness;
  uniform float uPlumeBrightness;
  uniform float uPlumeOpacity;
  uniform float uDecayFromTip;
  uniform float uTurbulence;
  uniform float uSpeed;
  uniform vec3  uColorInner;
  uniform vec3  uColorOuter;
  uniform vec3  uColorDiamond;

  varying vec3 vWorldPos;

  #define PI       3.14159265358979
  #define SCALING  0.12
  #define OMEGA_F  48.0

  // ── 3-D value noise (hermite-interpolated) ────────────────────────────────
  float hash3d(vec3 p) {
    float h = p.x + p.y * 37.0 + p.z * 521.0;
    return fract(sin(h * 1.333) * 100003.9);
  }
  float hermite(float a, float b, float t) {
    return mix(a, b, t * t * (3.0 - 2.0 * t));
  }
  float noise3(vec3 p) {
    vec3 i = floor(p);
    vec3 f = fract(p);
    return hermite(
      hermite(
        hermite(hash3d(i),             hash3d(i+vec3(1,0,0)), f.x),
        hermite(hash3d(i+vec3(0,1,0)), hash3d(i+vec3(1,1,0)), f.x), f.y),
      hermite(
        hermite(hash3d(i+vec3(0,0,1)), hash3d(i+vec3(1,0,1)), f.x),
        hermite(hash3d(i+vec3(0,1,1)), hash3d(i+vec3(1,1,1)), f.x), f.y),
      f.z);
  }

  // ── Pressure-derived quantities ───────────────────────────────────────────
  float plumeLimit() {
    return SCALING * sqrt(1.0 / max(uPressure, 0.001));
  }

  // Oscillating cross-section radius at longitudinal position x (0=nozzle, 1=tip)
  float bumps(float x) {
    float lim    = plumeLimit();
    float nozzle = uNozzleRadius;
    if (nozzle < lim) {
      float c     = 0.4;
      float start = log(max((lim - nozzle) / lim, 0.0001)) / log(c);
      return lim - lim * pow(c, start + x);
    } else {
      float bulge = nozzle - lim;
      float omega = OMEGA_F * bulge;
      return lim + bulge * abs(cos(x * omega));
    }
  }

  // Bright fringe band right at the oscillating shock boundary
  float fringe(float r, float x) {
    float radius = bumps(x);
    float dist   = abs(r) - radius;
    return max(1.0 - abs(dist) / 0.08, 0.0);
  }

  // HDR shock-node glow — asymmetric front/back disc shape
  float diamond(float r, float x) {
    float lim    = plumeLimit();
    float nozzle = uNozzleRadius;
    if (nozzle <= lim) return 0.0;

    float bulge     = nozzle - lim;
    float omega     = OMEGA_F * bulge;
    float phase     = omega * x + PI * 0.5;
    float dLong     = mod(phase - 0.3 * PI, PI) - 0.7 * PI;
    float frontLen  = lim / max(bulge * omega, 0.001);
    float backLen   = frontLen * 0.3;
    float dLen      = dLong > 0.0 ? backLen : frontLen;
    float dRadius   = lim * max(0.0, 1.0 - abs(dLong / max(omega, 0.001)) / dLen);
    float decay     = max(0.0, 1.0 - abs(dLong));

    return (0.1 / max(frontLen, 0.001))
         * (1.0 - smoothstep(dRadius - 0.05, dRadius, r))
         * decay;
  }

  // ── Ray-AABB intersection (slab method) ───────────────────────────────────
  vec2 rayBox(vec3 ro, vec3 rd, vec3 bMin, vec3 bMax) {
    vec3 inv  = 1.0 / rd;
    vec3 t0   = (bMin - ro) * inv;
    vec3 t1   = (bMax - ro) * inv;
    vec3 tMin = min(t0, t1);
    vec3 tMax = max(t0, t1);
    float tN  = max(max(tMin.x, tMin.y), tMin.z);
    float tF  = min(min(tMax.x, tMax.y), tMax.z);
    return vec2(tN, tF);
  }

  void main() {
    // ── Ray in local plume space ──────────────────────────────────────────────
    vec3 localCam  = (uModelMatrixInv * vec4(uCameraPos, 1.0)).xyz;
    vec3 localFrag = (uModelMatrixInv * vec4(vWorldPos,  1.0)).xyz;
    vec3 rd = normalize(localFrag - localCam);
    vec3 ro = localCam;

    // Bounding box: plume runs 0→1 on local X axis
    float maxR = max(uNozzleRadius, plumeLimit()) + 0.25;
    vec2 tb = rayBox(ro, rd, vec3(0.0, -maxR, -maxR), vec3(1.0, maxR, maxR));

    if (tb.y < tb.x || tb.y < 0.0) discard;

    float tStart = max(tb.x, 0.0);
    float tEnd   = tb.y;
    float tLen   = tEnd - tStart;

    // ── Ray march ────────────────────────────────────────────────────────────
    const int STEPS = 80;
    float ds = tLen / float(STEPS);

    vec3  accumulated  = vec3(0.0);
    float transmittance = 1.0;

    for (int i = 0; i < STEPS; i++) {
      vec3  p     = ro + rd * (tStart + (float(i) + 0.5) * ds);
      float x     = p.x;
      float rDist = length(p.yz);

      if (x < 0.0 || x > 1.0) continue;

      float radius = bumps(x);
      if (rDist > radius + 0.15) continue;

      // Axial decay — 1 at nozzle end (x=1 in local space), 0 at tip
      // uDecayFromTip=1 → strong at nozzle, fades toward tip
      float axialDecay = pow(clamp(mix(1.0, x, uDecayFromTip), 0.0, 1.0), 3.0);

      // Envelope fades — no hard box edges
      float tipFade    = 1.0 - smoothstep(0.72, 1.0,  x);
      float nozzleFade = smoothstep(0.0,  0.05, x);
      float decay      = (1.0 - x * 0.8) * tipFade * nozzleFade;

      // Density with radial edge softening
      float falloff  = clamp((radius - rDist) / 0.05, 0.0, 1.0);
      float edgeSoft = smoothstep(0.9, 0.45, rDist / max(radius, 0.001));
      float density  = 6.0 * falloff * edgeSoft * uPlumeIntensity * axialDecay;

      // 3D turbulence
      vec3  np   = p * vec3(8.0, 14.0, 14.0) + vec3(-uTime * uSpeed * 0.01, 0.0, 0.0);
      float turb = noise3(np);
      float atten = mix(1.0, 0.5 + turb, uTurbulence) * decay;

      // Colour: inner (white-hot) → outer (deep orange) by radial fraction, tinted by diamond colour
      float radFrac   = clamp(rDist / max(radius, 0.001), 0.0, 1.0);
      vec3  baseCol   = mix(uColorInner, uColorOuter, radFrac * radFrac);
      vec3  flameCol  = mix(baseCol, uColorDiamond, 0.5) * uPlumeBrightness;

      // Diamond & fringe — respect fades only, axial decay handled via density
      float d = diamond(rDist, x) * tipFade * nozzleFade;
      float f = fringe(rDist, x)  * tipFade * nozzleFade;
      vec3  diamCol = uColorDiamond * (d * uDiamondBrightness + f * 2.0);

      // Beer-Lambert accumulation
      float ext = exp(-density * ds);
      accumulated    += transmittance * (flameCol * density * ds * atten
                                       + diamCol  * density * ds * atten);
      transmittance  *= ext;

      if (transmittance < 0.005) break;
    }

    if (length(accumulated) < 0.001) discard;

    float alpha = smoothstep(0.0, 0.15, 1.0 - transmittance) * uPlumeOpacity;
    gl_FragColor = vec4(accumulated, alpha);
  }
`

// ---------------------------------------------------------------------------
// React component
// ---------------------------------------------------------------------------
export function MachDiamonds({
  nozzleRadius      = 0.18,
  pressure          = 0.08,
  plumeIntensity    = 1.2,
  plumeBrightness   = 1.0,
  plumeOpacity      = 1.0,
  decayFromTip      = 1.0,
  diamondBrightness = 8.0,
  turbulence        = 0.6,
  speed             = 40.0,
  colorInner        = new THREE.Color(1.0, 0.95, 0.7),
  colorOuter        = new THREE.Color(1.0, 0.28, 0.0),
  colorDiamond      = new THREE.Color(1.0, 0.85, 0.5),
  ...props
}) {
  const meshRef    = useRef()
  const { camera } = useThree()

  const material = useMemo(() => new THREE.ShaderMaterial({
    vertexShader,
    fragmentShader,
    uniforms: {
      uTime:             { value: 0 },
      uCameraPos:        { value: new THREE.Vector3() },
      uModelMatrixInv:   { value: new THREE.Matrix4() },
      uNozzleRadius:     { value: nozzleRadius },
      uPressure:         { value: pressure },
      uPlumeIntensity:   { value: plumeIntensity },
      uPlumeBrightness:  { value: plumeBrightness },
      uPlumeOpacity:     { value: plumeOpacity },
      uDecayFromTip:     { value: decayFromTip },
      uDiamondBrightness:{ value: diamondBrightness },
      uTurbulence:       { value: turbulence },
      uSpeed:            { value: speed },
      uColorInner:       { value: colorInner.clone() },
      uColorOuter:       { value: colorOuter.clone() },
      uColorDiamond:     { value: colorDiamond.clone() },
    },
    transparent: true,
    blending:    THREE.AdditiveBlending,
    side:        THREE.BackSide,
    depthWrite:  false,
  }), [])

  useEffect(() => () => material.dispose(), [material])

  useEffect(() => {
    material.uniforms.uNozzleRadius.value      = nozzleRadius
    material.uniforms.uPressure.value          = pressure
    material.uniforms.uPlumeIntensity.value    = plumeIntensity
    material.uniforms.uPlumeBrightness.value   = plumeBrightness
    material.uniforms.uPlumeOpacity.value      = plumeOpacity
    material.uniforms.uDecayFromTip.value      = decayFromTip
    material.uniforms.uDiamondBrightness.value = diamondBrightness
    material.uniforms.uTurbulence.value        = turbulence
    material.uniforms.uSpeed.value             = speed
    material.uniforms.uColorInner.value.set(colorInner)
    material.uniforms.uColorOuter.value.set(colorOuter)
    material.uniforms.uColorDiamond.value.set(colorDiamond)
  }, [nozzleRadius, pressure, plumeIntensity, plumeBrightness, plumeOpacity, decayFromTip, diamondBrightness, turbulence, speed,
      colorInner, colorOuter, colorDiamond, material])

  useFrame((_, delta) => {
    const t = (material.uniforms.uTime.value += delta)
    // Fast flicker: sum two incommensurate sine waves for pseudo-random feel
    const flicker = Math.sin(t * 47.3) * 0.5 + Math.sin(t * 83.7) * 0.5
    material.uniforms.uPressure.value = pressure + flicker * (pressure * 0.01)
    material.uniforms.uCameraPos.value.copy(camera.position)
    if (meshRef.current) {
      material.uniforms.uModelMatrixInv.value
        .copy(meshRef.current.matrixWorld)
        .invert()
    }
  })

  return (
    <group {...props} dispose={null}>
      {/* Single box — ray march runs inside the fragment shader.
          Local X runs 0 (nozzle) → 1 (tip). Offset +0.5 so the group
          origin sits at the nozzle exit. BackSide so the box faces render
          from outside and the camera can enter the volume. */}
      <mesh ref={meshRef} position={[0.5, 0, 0]}>
        <boxGeometry args={[2, 2, 2]} />
        <primitive object={material} attach="material" />
      </mesh>
    </group>
  )
}
